import { useCallback, useEffect, useState } from 'react';
import { omit } from 'underscore';
import deepEqual from 'fast-deep-equal';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import BottomSheet from '@ui/bottom-sheet/bottom-sheet';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import ThemePackCarousel from './theme-pack-carousel/theme-pack-carousel';
import ThemeModeToggle from '../theme-mode-toggle/theme-mode-toggle';
import {
  CreateThemePack,
  EColorVariablesV2,
  EPaletteModes,
  ThemePack,
  UpdateThemePack,
} from '@/types/theme-packs.types';
import ThemePackColorVariantChange from '../theme-pack-color-variant-change/theme-pack-color-variant-change';
import ThemePackFontVariantChange from '../theme-pack-font-variant-change/theme-pack-font-variant-change';
import ThemePackCornersChange from '../theme-pack-corners-change/theme-pack-corners-change';
import { Case, SwitchCase } from '@ui/switch-case/switch-case';
import ThemePackColorVariantButton from '../theme-pack-color-variant-change/theme-pack-color-variant-button';
import ThemePackFontVariantButton from '../theme-pack-font-variant-change/theme-pack-font-variant-button';
import ThemePackCornersButton from '../theme-pack-corners-change/theme-pack-corners-button';
import { selectThemePacks } from '@/store/slices/templates/selectors';
import OptionalComponent from '@ui/optional-component/optional-component';
import { appendNewTheme, setThemePack } from '@/store/slices/templates';
import ThemePackSaveModal from '../theme-pack-save-modal/theme-pack-save-modal';
import toastTrigger from '@ui/toast/toast-trigger';
import { CreateNewColorPack, SaveColor } from '@/connection/theme-packs';
import { FetchError } from '@/connection/helpers';
import { ConfirmationModal } from '@/micros/shared-create-and-edit-components/confirmation-modal';
import ThemePackImageChange from '../theme-pack-image-change/theme-pack-image-change';
import ThemePackImageButton from '../theme-pack-image-change/theme-pack-image-button';
import ThemePackDeleteModal from '../theme-pack-delete-modal/theme-pack-delete-modal';
import ThemePackSecondaryColorButton from '../theme-pack-secondary-color-type/theme-pack-secondary-color-button';
import ThemePackSecondaryColorChange from '../theme-pack-secondary-color-type/theme-pack-secondary-color-change';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';

import styles from './theme-tray.module.css';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { EToastTypes } from '@ui/toast/toast.types';

enum Tabs {
  main,
  colorPrimary,
  colorAccent,
  colorSecondary,
  font,
  corners,
  logo,
}

type TThemeTrayProps = {
  onThemeModeChange: (mode: EPaletteModes) => void;
  onThemePackChange: (pack: ThemePack) => void;
  onThemePackUpdate: (pack: ThemePack) => void;
  onClose: () => void;
  open: boolean;
  activeThemePack: ThemePack;
  activeThemeMode: EPaletteModes;
  saveId?: string;
  editable?: boolean;
  /** overrides just the Font tab's editability -- defaults to `editable` if not set. Lets a
   * context disable color/corners/logo editing while still allowing font selection. */
  editableFont?: boolean;
  /** overrides just the Corners tab's editability -- defaults to `editable` if not set. */
  editableCorners?: boolean;
  /** overrides just the color tabs' (Primary/Accents/Secondary) editability -- defaults to
   * `editable` if not set. */
  editableColors?: boolean;
  /** force every save from this tray to fork a new theme pack instead of overwriting the
   * shared one in place -- for contexts (like the campaigns editor) that should never mutate
   * a theme pack used elsewhere in the project. */
  forceSaveAsNew?: boolean;
  /** overrides the tray's portal stacking order -- see BottomSheet's `zIndex` prop */
  portalZIndex?: number;
  /** hide the Light/Dark toggle -- for contexts where mode isn't independently configurable */
  showModeToggle?: boolean;
  /** hide the Corners tab -- for contexts where a theme pack's corner style doesn't apply */
  showCornersTab?: boolean;
  /** hide the Logo tab -- for contexts where a theme pack's logo doesn't apply */
  showLogoTab?: boolean;
};

const ThemeTray = ({
  saveId,
  activeThemeMode,
  activeThemePack,
  onThemeModeChange,
  onThemePackChange,
  onThemePackUpdate,
  onClose,
  open,
  editable = true,
  editableFont,
  editableCorners,
  editableColors,
  forceSaveAsNew = false,
  portalZIndex,
  showModeToggle = true,
  showCornersTab = true,
  showLogoTab = true,
}: TThemeTrayProps) => {
  const channelThemes = useAppSelector(selectThemePacks);
  const dispatch = useAppDispatch();
  const [tab, setTab] = useState<Tabs>(Tabs.main);
  const [changesExist, setChangesExist] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [navModalOpen, setNavModalOpen] = useState(false);
  const [height, setHeight] = useState('176px');
  const [trayOpened, setTrayOpened] = useState(false);
  const [tempSelectedTheme, setTempSelectedTheme] = useState<ThemePack | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const themePacks = useAppSelector(selectThemePacks);

  useEffect(() => {
    if (!activeThemePack || !channelThemes) {
      return;
    }

    const originalTheme = channelThemes.find((theme) => theme.color_pack === activeThemePack.color_pack);

    // these fields get changed on a save so we're removing them since they don't really matter here anyway
    const activeThemePackCopy = omit(activeThemePack, [
      'updated_at',
      'module_id',
      'event_id',
      'parent_id',
      'creation_type',
      'id',
    ]);

    const originalThemeCopy = omit(originalTheme, [
      'updated_at',
      'module_id',
      'event_id',
      'parent_id',
      'creation_type',
      'id',
    ]);

    setChangesExist(!!originalTheme && !deepEqual(activeThemePackCopy, originalThemeCopy));
  }, [activeThemePack, channelThemes]);

  useEffect(() => {
    if (open) {
      setTab(Tabs.main);
    }
  }, [open]);

  const selectTab = useCallback(
    (newTab: Tabs) => () => {
      setTab((current) => (current === newTab ? Tabs.main : newTab));
    },
    []
  );

  const cancel = useCallback(() => {
    const originalTheme = channelThemes?.find((theme) => theme.color_pack === activeThemePack.color_pack);
    if (originalTheme) {
      onThemePackChange(originalTheme);
      setChangesExist(false);
      setTab(Tabs.main);
    }
    setSaveModalOpen(false);
  }, [activeThemePack.color_pack, channelThemes, onThemePackChange]);

  const onSave = useCallback(() => {
    setSaveModalOpen(true);
  }, []);

  const saveAsNew = useCallback(
    async (name: string, savingTheme: ThemePack, setAsCurrent = true) => {
      try {
        const toSave: CreateThemePack = {
          ...savingTheme,
          font_pack: savingTheme.font_pack?.font_pack,
          title: name,
        };

        const newThemePack = await CreateNewColorPack(toSave);
        newThemePack.updated_at = new Date().toISOString();
        dispatch(appendNewTheme({ theme: newThemePack, after: savingTheme.color_pack }));
        setSaveModalOpen(false);

        if (setAsCurrent) {
          onThemePackChange(newThemePack);
          setChangesExist(false);
          setTab(Tabs.main);
        }
      } catch (e) {
        console.error(e);
        if (e instanceof FetchError && e.response.status === 400) {
          toastTrigger(e.body?.error ?? 'Unable to save theme', {
            type: EToastTypes.error,
          });
          return;
        }
        toastTrigger('Unable to save theme', {
          type: EToastTypes.error,
        });
      } finally {
        setSaving(false);
      }
    },
    [dispatch, onThemePackChange]
  );

  const saveInPlace = useCallback(
    async (name: string, savingTheme: ThemePack) => {
      try {
        setSaving(true);
        const toSave: UpdateThemePack = {
          ...savingTheme,
          title: name,
          font_pack: savingTheme.font_pack?.font_pack,
        };

        const updated = await SaveColor(toSave);
        updated.updated_at = new Date().toISOString();
        dispatch(setThemePack(updated));
        setSaveModalOpen(false);
        setTempSelectedTheme(null);

        // if altering the theme that's selected, update the entire view
        // and reset changesExist to false
        if (savingTheme.color_pack === activeThemePack.color_pack) {
          onThemePackChange(updated);
          setChangesExist(false);
          setTab(Tabs.main);
        }
      } catch (e) {
        console.error(e);
        if (e instanceof FetchError && e.response.status === 400) {
          toastTrigger(e.body?.error ?? 'Unable to save theme', {
            type: EToastTypes.error,
          });
          return;
        }
        toastTrigger('Unable to save theme', {
          type: EToastTypes.error,
        });
      } finally {
        setSaving(false);
      }
    },
    [activeThemePack.color_pack, dispatch, onThemePackChange]
  );

  const handleDuplicate = useCallback(
    (savingTheme: ThemePack) => {
      const getName = (name: string) => {
        if ((themePacks || []).some((pack) => pack.title === name)) {
          return getName(name + ' Copy');
        }

        return name;
      };
      saveAsNew(`${getName(`${savingTheme.title} - Copy`)}`, savingTheme, false);
    },
    [saveAsNew, themePacks]
  );

  const handleSave = useCallback(
    async (name: string, asNew: boolean) => {
      const savingTheme = tempSelectedTheme || activeThemePack;

      if (!asNew && savingTheme.channel > 0) {
        saveInPlace(name, savingTheme);
        return;
      }

      saveAsNew(name, savingTheme);
    },
    [activeThemePack, saveAsNew, saveInPlace, tempSelectedTheme]
  );

  const handleCloseTray = (event?: MouseEvent) => {
    if (
      event?.target instanceof Element &&
      (event.target.closest('.alter-theme') || event.target.closest('.global-modal-body'))
    ) {
      return;
    }

    if (changesExist && !saveModalOpen) {
      setNavModalOpen(true);
    }
    if (!changesExist) {
      if (
        !(event?.target instanceof HTMLElement) ||
        (!event?.target.closest('button[name="theme-info-toggle"]') && open)
      ) {
        onClose();
      }
    }
  };

  const modifyTheme = useCallback(
    (theme: ThemePack, action: 'edit' | 'duplicate' | 'delete') => {
      setTempSelectedTheme(theme);
      switch (action) {
        case 'edit': {
          setSaveModalOpen(true);
          break;
        }
        case 'duplicate': {
          handleDuplicate(theme);
          break;
        }
        case 'delete': {
          setShowDeleteModal(true);
          break;
        }
      }
    },
    [handleDuplicate]
  );

  useEffect(() => {
    setHeight(tab === Tabs.logo ? '192px' : '176px');
  }, [tab]);

  return (
    <BottomSheet
      open={open}
      height={height}
      width="100%"
      maxHeight="100vh"
      portalClassName={`z-10 ${styles['theme-tray-bottom-sheet-portal']}`}
      zIndex={portalZIndex}
      unmountContentIfHidden={false}
      startUnmounted
      trapFocus
      onClickAway={handleCloseTray}
      onEscape={() => {
        if (tab === Tabs.main) {
          handleCloseTray();
        } else {
          setTab(Tabs.main);
        }
      }}
      onOpened={() => {
        setTrayOpened(true);
      }}
      onClosed={() => {
        setTrayOpened(false);
      }}
    >
      <Container
        background="rgba(var(--gray-900-rgb), 0.05)"
        height={height}
        padding="12px 0 24px 0"
        maxHeight="100%"
        width="100%"
        className={`flex jc-center ai-center fd-column ${styles['theme-tray-focus-outline']}`}
        data-component="ThemeTray"
      >
        <Button
          onClick={() => {
            if (tab === Tabs.main) {
              handleCloseTray();
            } else {
              setTab(Tabs.main);
            }
          }}
          backgroundColor="transparent"
          className="flex jc-center ai-center margin-t-12 padding-12 margin-b-8 padding-12 cursor-pointer"
        >
          <Container
            background="var(--gray-300)"
            hoverBackground="var(--gray-200)"
            className="flex jc-center clear-button radius-100 padding-h-24"
            height="4px"
            width="37px"
          />
        </Button>

        <Container background="transparent" padding="0 24px" height="100%" width="100%">
          <Container
            background="transparent"
            margin="0 auto 12px auto"
            maxWidth="100vw"
            className="flex gap-12"
            data-component="ThemeTray-upper"
          >
            <Container
              background="transparent"
              width="calc(var(--main-width-lhmp) + 20px)"
              padding="0 20px"
              margin="auto"
              maxWidth="100%"
              className="flex gap-12 overflow-auto"
            >
              <SwitchCase input={tab}>
                <Case value={Tabs.main}>
                  <ThemePackCarousel
                    activeThemePack={activeThemePack}
                    themeMode={activeThemeMode}
                    onThemePackChange={onThemePackChange}
                    saveId={saveId}
                    scrollToActiveThemePack
                    trayOpen={trayOpened}
                    modifyTheme={modifyTheme}
                    editable={editable}
                  />
                </Case>
                <Case value={Tabs.colorPrimary}>
                  <ThemePackColorVariantChange
                    variant={EColorVariablesV2.primary}
                    themeMode={activeThemeMode}
                    themePack={activeThemePack}
                    onThemePackUpdate={onThemePackUpdate}
                  />
                </Case>
                <Case value={Tabs.colorSecondary}>
                  <ThemePackSecondaryColorChange
                    themeMode={activeThemeMode}
                    themePack={activeThemePack}
                    onThemePackUpdate={onThemePackUpdate}
                  />
                </Case>
                <Case value={Tabs.colorAccent}>
                  <ThemePackColorVariantChange
                    variant={EColorVariablesV2.accents}
                    themeMode={activeThemeMode}
                    themePack={activeThemePack}
                    onThemePackUpdate={onThemePackUpdate}
                  />
                </Case>
                <Case value={Tabs.font}>
                  <ThemePackFontVariantChange
                    themePack={activeThemePack}
                    onThemePackUpdate={onThemePackUpdate}
                    scrollToActiveFontPack
                  />
                </Case>
                <Case value={Tabs.corners}>
                  <ThemePackCornersChange themePack={activeThemePack} onThemePackUpdate={onThemePackUpdate} />
                </Case>
                <Case value={Tabs.logo}>
                  <ThemePackImageChange themePack={activeThemePack} onThemePackUpdate={onThemePackUpdate} />
                </Case>
              </SwitchCase>
            </Container>
          </Container>

          <Container maxWidth="100%" margin="0 auto" width="min-content" className="overflow-auto">
            <Container
              className="flex gap-8 jc-center"
              width="100%"
              aspectRatio="2/1"
              maxWidth="100%"
              data-component="ThemeTray-lower"
              minWidth="fit-content"
            >
              <Container
                className="flex gap-8 jc-center ai-center radius-100"
                height="48px"
                width="fit-content"
                padding="8px"
                borderWidth="1px"
                borderColor={changesExist ? 'rgba(var(--gray-100-rgb), 0.1)' : 'transparent'}
                background={changesExist ? 'rgba(var(--gray-1000-rgb), 0.2)' : 'transparent'}
              >
                <OptionalComponent display={showModeToggle}>
                  <Container background="transparent" width="64px">
                    <ThemeModeToggle activeMode={activeThemeMode} onChange={onThemeModeChange} />
                  </Container>
                </OptionalComponent>
                <Container background="transparent">
                  <ThemePackColorVariantButton
                    variant={EColorVariablesV2.primary}
                    themeMode={activeThemeMode}
                    themePack={activeThemePack}
                    selectTab={selectTab(Tabs.colorPrimary)}
                    active={tab === Tabs.colorPrimary}
                    disabled={!(editableColors ?? editable)}
                  />
                </Container>
                <Container background="transparent">
                  <ThemePackColorVariantButton
                    variant={EColorVariablesV2.accents}
                    themeMode={activeThemeMode}
                    themePack={activeThemePack}
                    selectTab={selectTab(Tabs.colorAccent)}
                    active={tab === Tabs.colorAccent}
                    disabled={!(editableColors ?? editable)}
                  />
                </Container>
                <Container background="transparent">
                  <ThemePackSecondaryColorButton
                    themePack={activeThemePack}
                    themeMode={activeThemeMode}
                    selectTab={selectTab(Tabs.colorSecondary)}
                    active={tab === Tabs.colorSecondary}
                    disabled={!(editableColors ?? editable)}
                  />
                </Container>
                <Container background="transparent">
                  <ThemePackFontVariantButton
                    themePack={activeThemePack}
                    active={tab === Tabs.font}
                    selectTab={selectTab(Tabs.font)}
                    disabled={!(editableFont ?? editable)}
                  />
                </Container>
                <OptionalComponent display={showCornersTab}>
                  <Container background="transparent">
                    <ThemePackCornersButton
                      selectTab={selectTab(Tabs.corners)}
                      active={tab === Tabs.corners}
                      disabled={!(editableCorners ?? editable)}
                    />
                  </Container>
                </OptionalComponent>
                <OptionalComponent display={showLogoTab}>
                  <Container background="transparent">
                    <ThemePackImageButton
                      selectTab={selectTab(Tabs.logo)}
                      active={tab === Tabs.logo}
                      disabled={!editable}
                    />
                  </Container>
                </OptionalComponent>
                <OptionalComponent display={changesExist}>
                  <Container background="transparent" className="flex gap-8 ai-center margin-l-40">
                    <Text className="C1" whiteSpace="nowrap">
                      Save changes?
                    </Text>
                    <Button
                      onClick={cancel}
                      backgroundColor="var(--gray-650)"
                      hoverBackgroundColor="var(--gray-600)"
                      height="32px"
                      minWidth="76px"
                      padding="8px 16px"
                    >
                      <Text color="var(--gray-true-dark-100)" className="L1">
                        Cancel
                      </Text>
                    </Button>
                    <Button
                      backgroundColor={getThemeColorCSSVariable()}
                      height="32px"
                      padding="8px 16px"
                      onClick={onSave}
                      className="flex ai-center jc-center"
                    >
                      <Text
                        color={getThemeColorContrastCSSVariable(
                          EColorVariablesV2.buttonText,
                          EColorVariablesV2.primary
                        )}
                        className="L1"
                        whiteSpace="nowrap"
                      >
                        Save
                      </Text>
                    </Button>
                  </Container>
                </OptionalComponent>
              </Container>
            </Container>
          </Container>
        </Container>
      </Container>
      <ThemePackSaveModal
        open={saveModalOpen}
        close={cancel}
        activeThemeMode={tempSelectedTheme?.theme_mode || activeThemeMode}
        activeThemePack={tempSelectedTheme || activeThemePack}
        onSave={handleSave}
        saving={saving}
        editingNonSelectedTheme={!!tempSelectedTheme}
        forceSaveAsNew={forceSaveAsNew}
      />
      <ThemePackDeleteModal
        open={showDeleteModal}
        close={() => {
          setShowDeleteModal(false);
          setTempSelectedTheme(null);
        }}
        tempSelectedTheme={tempSelectedTheme}
      />
      <ConfirmationModal
        showModal={navModalOpen}
        heading="Leave without saving?"
        content="You will lose any changes to your theme."
        primaryButtonText="Continue editing"
        secondaryButtonText="Discard changes"
        onPrimaryAction={() => setNavModalOpen(false)}
        onSecondaryAction={() => {
          setNavModalOpen(false);
          onClose();
          cancel();
        }}
      />
    </BottomSheet>
  );
};

export default ThemeTray;
